import apiClient from './api-client';

export interface User {
    id: number;
    email: string;
    name: string;
    first_name?: string;
    last_name?: string;
    phone?: string;
    phone_verified?: boolean;
    role: string;
    created_at?: string;
    two_factor_enabled?: boolean;
    two_factor_methods?: string;
    telegram_id?: string;
    telegram_verified?: boolean;
}

export interface LoginResponse {
    message: string;
    access_token?: string;
    refresh_token?: string;
    user?: User;
    '2fa_required'?: boolean;
    /**
     * Opaque, short-lived (5 min) ticket proving the password step succeeded.
     * Replaces the former `user_id`: returning a raw user id let a caller
     * skip the password entirely and brute-force the code against any account.
     */
    ticket?: string;
    two_factor_methods?: string;
    method_statuses?: Record<string, string>;
}

/**
 * Login with email and password.
 * Returns the full response — caller must check for 2fa_required.
 */
export async function login(email: string, password: string): Promise<LoginResponse> {
    return apiClient.post<LoginResponse>('/auth/login', { email, password });
}

/**
 * Login with Telegram Mini App initData
 */
export async function telegramLogin(initData: string): Promise<LoginResponse> {
    return apiClient.post<LoginResponse>('/auth/telegram-login', { initData });
}

/**
 * Verify 2FA code during login
 */
export async function verify2FA(ticket: string, code: string, method: string): Promise<LoginResponse> {
    return apiClient.post<LoginResponse>('/auth/verify-2fa', { ticket, code, method });
}

/**
 * Resend 2FA code during login (unauthenticated)
 */
export async function sendLoginCode(ticket: string, method: string): Promise<void> {
    await apiClient.post('/auth/send-code', { ticket, method });
}

/**
 * Logout - clears auth cookies on the backend
 */
export async function logout(): Promise<void> {
    await apiClient.post('/auth/logout');
}

/**
 * Get current authenticated user info
 */
export async function getMe(): Promise<User> {
    return apiClient.get<User>('/auth/me');
}

/**
 * Refresh the access token using the refresh token cookie
 */
export async function refreshToken(): Promise<void> {
    await apiClient.post('/auth/refresh');
}
